import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import Layout from '@/components/Layout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

export default function BlogPage() {
  const { language } = useLanguage();
  const { settings: siteSettings } = useSiteSettings();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/blog/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const s = siteSettings || {};
  const title = language === 'en' ? (s.blog_title_en || 'News & Blog') : (s.blog_title || 'News & Blog');
  const subtitle = language === 'en' ? (s.blog_subtitle_en || 'News from the Automotive World') : (s.blog_subtitle || 'Neuigkeiten aus der Automobilwelt');

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'en' ? 'en-US' : 'de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Extract excerpt from HTML content
  const getExcerpt = (html, maxLength = 150) => {
    const text = html.replace(/<[^>]+>/g, '');
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Layout>
      <div className="min-h-screen bg-theme" data-testid="blog-page">
        {/* Hero Section */}
        <section className="bg-theme-surface py-16 md:py-24 border-b border-theme">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 text-center">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="overline text-primary mb-4"
            >
              {subtitle}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-heading text-4xl md:text-5xl lg:text-6xl text-theme font-light"
            >
              {title}
            </motion.h1>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="py-16 md:py-24">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12 text-theme-muted">
                <p>{language === 'en' ? 'No posts yet.' : 'Noch keine Beiträge.'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-theme-surface rounded-lg overflow-hidden border border-theme hover:shadow-lg transition-all group"
                    data-testid={`blog-post-${index}`}
                  >
                    {/* Featured Image */}
                    <Link to={`/blog/${post.id}`} className="block">
                      <div className="aspect-[16/10] bg-theme-muted relative overflow-hidden">
                        {post.image_url ? (
                          <img
                            src={getImageUrl(post.image_url)}
                            alt={language === 'en' ? (post.title_en || post.title) : post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-theme-muted">
                            <span className="text-4xl">📰</span>
                          </div>
                        )}
                      </div>
                    </Link>

                    {/* Post Info */}
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-xs text-theme-muted mb-3">
                        <Calendar size={12} />
                        {formatDate(post.created_at)}
                      </div>
                      
                      <Link to={`/blog/${post.id}`}>
                        <h2 className="font-heading text-xl mb-3 text-theme group-hover:text-primary transition-colors">
                          {language === 'en' ? (post.title_en || post.title) : post.title}
                        </h2>
                      </Link>
                      
                      <p className="text-sm text-theme-muted mb-4 line-clamp-3">
                        {post.excerpt || getExcerpt(language === 'en' ? (post.content_en || post.content) : post.content)}
                      </p>
                      
                      <Link
                        to={`/blog/${post.id}`}
                        className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:gap-3 transition-all"
                      >
                        {language === 'en' ? 'Read More' : 'Weiterlesen'}
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </Layout>
  );
}
